// Admin API client for the Ludic Drive Assetstore backend.
// All endpoints under /api/admin/* require an admin role token.

import { API_URL } from '../config';
import { getAccessToken } from './token';

const API_BASE_URL = API_URL;

class ApiError extends Error {
  status?: number;
  detail?: string;

  constructor(message: string, status?: number, detail?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.detail = detail;
  }
}

async function request<T>(endpoint: string, options?: RequestInit): Promise<T> {
  const headers: Record<string, string> = {
    ...(options?.headers as Record<string, string> | undefined),
  };

  const token = getAccessToken() || undefined;
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers,
    });

    if (response.status === 204) {
      return undefined as T;
    }

    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      const error = new ApiError(
        response.statusText || 'Request failed',
        response.status,
        (data as { detail?: string })?.detail || (data as { message?: string })?.message
      );
      throw error;
    }

    return data as T;
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }
    throw new ApiError('Network error', undefined, String(error));
  }
}

function queryString(params?: Record<string, string | number | undefined>): string {
  const search = new URLSearchParams();
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== '') {
        search.append(key, String(value));
      }
    });
  }
  const qs = search.toString();
  return qs ? `?${qs}` : '';
}

export const api = {
  base: API_BASE_URL,

  // User / vendor management
  users: {
    list: (params?: { role?: string; search?: string; limit?: number; offset?: number }) =>
      request<{ users: AdminUser[]; total: number }>(`/api/admin/users${queryString(params)}`),
    update: (id: number, payload: Partial<UserUpdate>) =>
      request<AdminUser>(`/api/admin/users/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      }),
    approve: (id: number) =>
      request<AdminUser>(`/api/admin/users/${id}/approve`, { method: 'POST' }),
    reject: (id: number) => request<AdminUser>(`/api/admin/users/${id}/reject`, { method: 'POST' }),
    delete: (id: number) => request<void>(`/api/admin/users/${id}`, { method: 'DELETE' }),
  },

  // Product moderation
  products: {
    list: (params?: {
      status_filter?: string;
      search?: string;
      vendor_id?: number;
      limit?: number;
      offset?: number;
    }) =>
      request<{ products: AdminProduct[]; total: number }>(
        `/api/admin/products${queryString(params)}`
      ),
    pending: () =>
      request<{ products: AdminProduct[]; total: number }>('/api/admin/products/pending'),
    get: (id: number) => request<AdminProduct>(`/api/admin/products/${id}`),
    approve: (id: number) =>
      request<AdminProduct>(`/api/admin/products/${id}/approve`, { method: 'POST' }),
    reject: (id: number, reason?: string) =>
      request<AdminProduct>(`/api/admin/products/${id}/reject`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason }),
      }),
    update: (id: number, payload: Partial<ProductUpdatePayload>) =>
      request<AdminProduct>(`/api/admin/products/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      }),
    delete: (id: number) => request<void>(`/api/admin/products/${id}`, { method: 'DELETE' }),
  },

  // Orders + exports
  orders: {
    list: (params?: { status_filter?: string; search?: string; limit?: number; offset?: number }) =>
      request<{ orders: AdminOrder[]; total: number }>(`/api/admin/orders${queryString(params)}`),
    refund: (id: number, payload?: { amount?: number; reason?: string }) =>
      request<AdminOrder>(`/api/admin/orders/${id}/refund`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload ?? {}),
      }),
    exportCsvUrl: () => `${API_BASE_URL}/api/admin/orders/export.csv`,
    exportPdfUrl: () => `${API_BASE_URL}/api/admin/orders/export.pdf`,
  },

  // Analytics
  stats: {
    get: () => request<AdminStats>('/api/admin/stats'),
    revenue: (days: number = 30) =>
      request<RevenuePoint[]>(`/api/admin/stats/revenue${queryString({ days })}`),
  },

  // Payouts
  payouts: {
    list: () => request<AdminPayout[]>('/api/admin/payouts'),
    updateStatus: (id: number, status: string) =>
      request<AdminPayout>(`/api/admin/payouts/${id}/status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      }),
  },

  // Platform settings
  settings: {
    get: () => request<PlatformSettings>('/api/admin/settings'),
    update: (payload: Partial<PlatformSettings>) =>
      request<PlatformSettings>('/api/admin/settings', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      }),
  },

  // Vouchers
  vouchers: {
    list: (params?: { limit?: number; offset?: number }) =>
      request<{ vouchers: AdminVoucher[]; total: number }>(
        `/api/admin/vouchers${queryString(params)}`
      ),
    create: (payload: VoucherCreatePayload) =>
      request<AdminVoucher>('/api/admin/vouchers', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      }),
    update: (
      id: number,
      payload: { is_active?: boolean; max_uses?: number | null; expires_at?: string | null }
    ) =>
      request<AdminVoucher>(`/api/admin/vouchers/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      }),
  },
};

export interface AdminUser {
  id: number;
  keycloak_id: string;
  email: string;
  name: string;
  role: string;
  is_vendor_approved: boolean;
  vendor_id: number | null;
  avatar_url: string | null;
  created_at: string;
  updated_at: string;
}

export interface UserUpdate {
  role?: string;
  is_vendor_approved?: boolean;
  name?: string;
  email?: string;
  vendor_id?: number;
}

export interface AdminProductAttribute {
  id: number;
  name: string;
  value: string;
}

export interface AdminProductVersion {
  id: number;
  version_name: string | null;
  ue_version: string;
  price: number | null;
  dev_compatibility: boolean;
  runtime_compatibility: boolean;
  changelog: string | null;
  file_size: number | null;
}

export interface AdminProduct {
  id: number;
  name: string;
  description: string;
  price: number;
  vendor_id: number;
  vendor_name: string;
  category: string;
  thumbnail_url: string | null;
  is_featured: boolean;
  images: string[] | null;
  youtube_url: string | null;
  status: string;
  rejection_reason: string | null;
  created_at: string;
  updated_at: string;
  attributes: AdminProductAttribute[];
  versions: AdminProductVersion[];
}

export interface ProductUpdatePayload {
  name?: string;
  description?: string;
  price?: number;
  category?: string;
  thumbnail_url?: string;
  is_featured?: boolean;
  youtube_url?: string;
  status?: string;
}

export interface AdminOrderItem {
  id: number;
  product_id: number;
  product_name: string;
  quantity: number;
  price: number;
}

export interface AdminOrder {
  id: number;
  user_id: number;
  user_name: string;
  email: string;
  status: string;
  total_amount: number;
  subtotal_amount?: number;
  discount_amount?: number;
  tax_amount?: number;
  tax_rate?: number;
  vat_exempt?: boolean;
  currency?: string;
  display_subtotal?: number;
  display_discount?: number;
  display_total?: number;
  created_at: string;
  updated_at: string;
  items: AdminOrderItem[];
  refund_status?: string | null;
  refund_amount?: number | null;
}

export interface AdminStats {
  total_users: number;
  total_vendors: number;
  pending_vendors: number;
  total_products: number;
  pending_products: number;
  total_orders: number;
  monthly_revenue: number;
  total_revenue: number;
  pending_payouts: number;
}

export interface RevenuePoint {
  date: string;
  revenue: number;
  orders: number;
}

export interface AdminPayout {
  id: number;
  vendor_id: number;
  vendor_name: string;
  amount: number;
  status: string;
  method: string;
  note: string | null;
  created_at: string;
  updated_at: string;
}

export interface PlatformSettings {
  commission_rate: number;
  stripe_publishable_key: string;
  privacy_policy: string;
  terms_of_service: string;
  base_currency: string;
  default_currency: string;
  default_country: string;
  supported_currencies: string[];
  exchange_rates: Record<string, number>;
}

export interface AdminVoucher {
  id: number;
  code: string;
  discount_percent: number;
  product_ids: number[] | null;
  max_uses: number | null;
  usage_count: number;
  is_active: boolean;
  starts_at: string | null;
  expires_at: string | null;
  created_at: string;
}

export interface VoucherCreatePayload {
  code: string;
  discount_percent: number;
  product_ids?: number[] | null;
  max_uses?: number | null;
  starts_at?: string | null;
  expires_at?: string | null;
}

export type { ApiError };
